/**
 * Eine KIAN-Aktion laden, ändern, ausführen — und während des Laufs den Fortschritt abfragen.
 *
 * Während der Ausführung fragt die Karte alle 1,5 s den KOMPAKTEN Stand ab (ohne Entwürfe): bei
 * einem Import mit 300 Tickets wären die vollen Entwürfe jedes Mal ein halbes Megabyte. Die
 * Zustände werden in den vollen Stand eingemischt; nach dem Lauf wird einmal vollständig geladen.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { KianAction, KianActionPatch } from '../../../lib/api/domains/kianActions';

export const kianActionKey = (id: string) => ['kian-action', id] as const;
const liveKey = (id: string) => ['kian-action', id, 'live'] as const;

function merge(full: KianAction, live: KianAction | undefined): KianAction {
  if (!live || live.updatedAt < full.updatedAt) return full;
  const byId = new Map(live.items.map((it) => [it.id, it]));
  return {
    ...full,
    status: live.status,
    counts: live.counts,
    finishedAt: live.finishedAt,
    confirmedAt: live.confirmedAt,
    updatedAt: live.updatedAt,
    items: full.items.map((it) => {
      const l = byId.get(it.id);
      return l
        ? {
            ...it,
            status: l.status,
            step: l.step,
            ticketId: l.ticketId,
            ticketNumber: l.ticketNumber,
            commentsDone: l.commentsDone,
            error: l.error,
            warnings: l.warnings,
            attempts: l.attempts,
            queue: l.queue,
          }
        : it;
    }),
  };
}

export function useKianAction(id: string) {
  const qc = useQueryClient();
  const fullQuery = useQuery({
    queryKey: kianActionKey(id),
    queryFn: () => api.getKianAction(id).then((r) => r.action),
    staleTime: 10_000,
    // Wartet etwas auf weclapp, gelegentlich nachsehen — sonst stünde „wartet" noch da, wenn der
    // Nachsync längst durch ist.
    refetchInterval: (query) =>
      query.state.data?.items.some((i) => i.status === 'QUEUED' && (i.queue?.state ?? 'WAITING') === 'WAITING') ? 60_000 : false,
  });
  const running = fullQuery.data?.status === 'RUNNING';
  const liveQuery = useQuery({
    queryKey: liveKey(id),
    queryFn: () => api.getKianAction(id, { detail: false }).then((r) => r.action),
    enabled: running,
    refetchInterval: running ? 1500 : false,
  });

  // Lauf vorbei → einmal vollständig nachladen (Ticketnummern, Warnungen, Endstand)
  useEffect(() => {
    const liveStatus = liveQuery.data?.status;
    if (running && liveStatus && liveStatus !== 'RUNNING') {
      void qc.invalidateQueries({ queryKey: kianActionKey(id), exact: true });
    }
  }, [liveQuery.data?.status, running, qc, id]);

  const action = useMemo(
    () => (fullQuery.data ? merge(fullQuery.data, liveQuery.data) : undefined),
    [fullQuery.data, liveQuery.data],
  );

  const setAction = useCallback(
    (a: KianAction) => {
      qc.setQueryData(kianActionKey(id), a);
      if (a.status === 'RUNNING') qc.setQueryData(liveKey(id), a);
    },
    [qc, id],
  );

  const update = useMutation({
    mutationFn: (patch: KianActionPatch) => api.updateKianAction(id, patch).then((r) => r.action),
    onSuccess: setAction,
  });
  const execute = useMutation({
    mutationFn: (body?: { itemIds?: string[]; confirmUnknown?: boolean }) =>
      api.executeKianAction(id, body).then((r) => r.action),
    onSuccess: setAction,
  });
  const discard = useMutation({
    mutationFn: () => api.discardKianAction(id).then((r) => r.action),
    onSuccess: setAction,
  });

  return {
    action,
    isLoading: fullQuery.isLoading,
    error: fullQuery.error,
    update,
    execute,
    discard,
  };
}
